import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { getMyTwin, getMyTwinTimeline, refreshMyTwin } from "@/lib/learner-twin.functions";
import { LearnerTwinPanel } from "@/components/intelligence/learner-twin-panel";
import { LearningDNATimeline } from "@/components/intelligence/learning-dna-timeline";
import { parentRecommendations, type LearnerTwin } from "@/lib/learner-twin";

export const Route = createFileRoute("/learner/twin")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({
    meta: [
      { title: "My Learner Twin — MAKAI" },
      {
        name: "description",
        content:
          "Your Makai Learner Twin: how you learn best, what Makai has noticed, and how lessons, questions and revision adapt to you.",
      },
      { property: "og:title", content: "My Learner Twin — MAKAI" },
      {
        property: "og:description",
        content: "See how Makai adapts your lessons, questions and revision to the way you learn.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: TwinPage,
});

function TwinPage() {
  const qc = useQueryClient();
  const { data: twin, isLoading } = useQuery({
    queryKey: ["learner-twin"],
    queryFn: () => getMyTwin(),
  });
  const { data: timeline } = useQuery({
    queryKey: ["learner-twin", "timeline"],
    queryFn: () => getMyTwinTimeline(),
  });
  const refresh = useMutation({
    mutationFn: () => refreshMyTwin(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["learner-twin"] }),
  });

  return (
    <AppShell
      role="learner"
      title="My Learner Twin"
      subtitle="How Makai understands the way you learn"
    >
      <div className="mb-4 flex justify-end">
        <button
          type="button"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-semibold transition hover:bg-muted disabled:opacity-60"
        >
          <RefreshCw className={refresh.isPending ? "h-3.5 w-3.5 animate-spin" : "h-3.5 w-3.5"} />
          {refresh.isPending ? "Updating…" : "Update now"}
        </button>
      </div>

      {isLoading && <div className="text-sm text-muted-foreground">Loading your twin…</div>}

      {!isLoading && !twin && (
        <div className="rounded-2xl border border-dashed border-border bg-card p-6 text-sm text-muted-foreground">
          Makai has not built your twin yet. Complete a lesson, quiz or practice paper and it will appear here.
        </div>
      )}

      {twin && (
        <div className="space-y-5">
          <LearnerTwinPanel
            twin={twin as LearnerTwin}
            audience="learner"
            recommendations={parentRecommendations(twin as LearnerTwin).map((text, i) => ({
              key: `self_${i}`,
              text,
              urgency: "low" as const,
            }))}
          />
          <LearningDNATimeline points={(timeline ?? []) as any} />
          <p className="text-xs text-muted-foreground">
            Makai only stores learning information. It never makes medical, psychological or diagnostic
            claims, and everything here changes as you keep learning.
          </p>
        </div>
      )}
    </AppShell>
  );
}
